import { useMemo, useState } from 'react';
import { useAppRegistryStore, MediaIcon } from '@room/shared-ui';
import { getSessionToken } from '@room/frontend-sdk';
import './AppSuggestions.css';

/**
 * Shuffle array using Fisher-Yates and return first `count` items.
 */
function pickRandom<T>(arr: T[], count: number): T[] {
  if (arr.length <= count) return arr;
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, count);
}

interface AppSuggestionsProps {
  /** Max number of suggestions to show */
  count?: number;
  /** Open an app route (bundle or navigate) */
  onOpenApp: (route: string) => void;
}

export function AppSuggestions({ count = 6, onOpenApp }: AppSuggestionsProps) {
  const manifests = useAppRegistryStore((s) => s.manifests);
  const installedApps = useAppRegistryStore((s) => s.installedApps);
  const catalogLoaded = useAppRegistryStore((s) => s.catalogLoaded);
  const installApp = useAppRegistryStore((s) => s.installApp);
  const [installingId, setInstallingId] = useState<string | null>(null);

  const suggestions = useMemo(() => {
    const dynamicNotInstalled = Object.values(manifests).filter(
      (m) =>
        m.category === 'dynamic' &&
        m.tile &&
        !installedApps.includes(m.id),
    );
    return pickRandom(dynamicNotInstalled, count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifests, installedApps, count, catalogLoaded]);

  const handleInstall = async (appId: string) => {
    setInstallingId(appId);
    try {
      const token = getSessionToken();
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      await fetch(`/api/apps/${appId}/install`, { method: 'POST', headers });
      installApp(appId);
    } catch {
      // silently fail
    } finally {
      setInstallingId(null);
    }
  };

  // Show skeleton while catalog is loading
  if (!catalogLoaded) {
    return (
      <div className="app-suggestions">
        <div className="app-suggestions__scroll">
          {Array.from({ length: count }).map((_, i) => (
            <div key={i} className="app-suggestions__item">
              <div className="app-suggestions__icon app-suggestions__icon--skeleton" />
              <div className="app-suggestions__label-skeleton" />
              <div className="app-suggestions__btn-skeleton" />
            </div>
          ))}
          <div className="app-suggestions__item">
            <div className="app-suggestions__icon app-suggestions__icon--skeleton" />
            <div className="app-suggestions__label-skeleton" />
          </div>
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) return null;

  return (
    <div className="app-suggestions">
      <div className="app-suggestions__scroll">
        {suggestions.map((app) => (
          <div key={app.id} className="app-suggestions__item">
            <div
              className="app-suggestions__icon"
              onClick={() => onOpenApp(`/store?app=${app.id}`)}
            >
              {app.mediaIconPrompt ? (
                <MediaIcon
                  mediaId={app.id}
                  fallback={app.icon}
                  size={54}
                  radius={14}
                  className="app-suggestions__media-icon"
                />
              ) : (
                <span>{app.icon}</span>
              )}
            </div>
            <div className="app-suggestions__label">{app.tile!.label}</div>
            <button
              className="app-suggestions__btn-get"
              disabled={installingId === app.id}
              onClick={() => handleInstall(app.id)}
            >
              {installingId === app.id ? '...' : 'GET'}
            </button>
          </div>
        ))}
        <div
          className="app-suggestions__item app-suggestions__see-all"
          onClick={() => onOpenApp('/store')}
        >
          <div className="app-suggestions__icon app-suggestions__icon--more">
            <span>...</span>
          </div>
          <div className="app-suggestions__label">See All</div>
        </div>
      </div>
    </div>
  );
}
